import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { OrderLink, OrderLinkResponse } from '@/pages/OrderLinkPage'
import { patchJSON } from '@/lib/api'

export function OrderReduction({
  order,
  queryKey,
  reducePath,
}: {
  order: OrderLink
  queryKey: readonly unknown[]
  reducePath?: string
}) {
  const queryClient = useQueryClient()
  const [open, setOpen] = React.useState(false)
  const [selected, setSelected] = React.useState<number[]>(() =>
    order.attendees.filter((attendee) => attendee.checked_in).map((attendee) => attendee.seat),
  )
  const unnamed = order.attendees.filter((attendee) => !attendee.name)
  const [quantity, setQuantity] = React.useState(Math.max(1, unnamed.length - 1))
  const path = reducePath ?? ''
  const reduce = useMutation({
    mutationFn: () => {
      const dropped = unnamed.length
        ? order.attendees.slice(quantity)
        : order.attendees.filter(
            (attendee) => !attendee.checked_in && !selected.includes(attendee.seat),
          )
      const target = unnamed.length
        ? quantity
        : order.attendees.filter(
            (attendee) => attendee.checked_in || selected.includes(attendee.seat),
          ).length
      return patchJSON<OrderLinkResponse>(path, {
        reduction: { quantity: target, attendee_seats: dropped.map((attendee) => attendee.seat) },
      })
    },
    onSuccess: (response) => {
      queryClient.setQueryData(queryKey, response)
      setOpen(false)
      setSelected(
        response.order_link.attendees
          .filter((attendee: OrderLink['attendees'][number]) => attendee.checked_in)
          .map((attendee: OrderLink['attendees'][number]) => attendee.seat),
      )
    },
  })

  if (!reducePath || order.cancelled || order.event.cancelled) return null
  if (!order.reducible)
    return order.reduction_note ? (
      <p className="text-caption text-muted-foreground">{order.reduction_note}</p>
    ) : null
  if (!open)
    return (
      <Button type="button" variant="outline" className="h-8" onClick={() => setOpen(true)}>
        Reduce seats
      </Button>
    )

  return (
    <Card>
      <CardContent className="space-y-3 px-4 py-3">
        <p className="text-body-sm font-medium">Reduce seats</p>
        {unnamed.length ? (
          <div className="space-y-1">
            <Label htmlFor="reduction-quantity" className="text-caption">
              Keep seats
            </Label>
            <Input
              id="reduction-quantity"
              type="number"
              min="1"
              max={unnamed.length - 1}
              value={quantity}
              onChange={(event) => setQuantity(Number(event.target.value))}
              className="h-8"
            />
          </div>
        ) : (
          <div className="space-y-2">
            {order.attendees.map((attendee) => (
              <label key={attendee.seat} className="flex items-start gap-2 text-body-sm">
                <Checkbox
                  checked={attendee.checked_in || selected.includes(attendee.seat)}
                  disabled={attendee.checked_in}
                  onCheckedChange={(checked) =>
                    setSelected((current) =>
                      checked
                        ? [...current, attendee.seat]
                        : current.filter((seat) => seat !== attendee.seat),
                    )
                  }
                />
                <span>
                  Keep {attendee.name}
                  {attendee.checked_in && (
                    <span className="mt-0.5 block text-caption text-muted-foreground">
                      {attendee.name} is checked in — undo check-in first
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>
        )}
        {reduce.isError && <Alert>{reduce.error.message}</Alert>}
        <div className="flex gap-2">
          <Button
            type="button"
            className="h-8"
            onClick={() => reduce.mutate()}
            disabled={reduce.isPending}
          >
            {reduce.isPending ? 'Saving…' : 'Confirm reduction'}
          </Button>
          <Button type="button" variant="outline" className="h-8" onClick={() => setOpen(false)}>
            Keep registration
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
